import React, { useState } from 'react';
import { 
  Users, 
  BarChart3, 
  Settings, 
  FileText, 
  Calendar, 
  AlertTriangle,
  BookOpen,
  UserPlus
} from 'lucide-react';
import { AnalyticsDashboard } from '../components/analytics/AnalyticsDashboard';
import { CourseBuilder } from '../components/course/CourseBuilder';
import { UserManagement } from '../components/user/UserManagement';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Tabs } from '../components/ui/Tabs';

export default function AdminPanel() {
  const stats = {
    totalDrivers: 142,
    activeTraining: 28,
    completedHours: 3420,
    complianceRate: 94
  };

  const recentActivity = [
    { driver: 'John Smith', action: 'Completed CPC Module 1', time: '2 hours ago' },
    { driver: 'Sarah Jones', action: 'Started Hazard Perception', time: '4 hours ago' },
    { driver: 'Mike Brown', action: 'Failed attention check', time: '6 hours ago' },
    { driver: 'Emma Wilson', action: 'Completed full qualification', time: '1 day ago' }
  ];

  const overviewTab = (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card gradient hover>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Drivers</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalDrivers}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card gradient hover>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Training</p>
                <p className="text-3xl font-bold text-gray-900">{stats.activeTraining}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card gradient hover>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed Hours</p>
                <p className="text-3xl font-bold text-gray-900">{stats.completedHours.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card gradient hover>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Compliance Rate</p>
                <p className="text-3xl font-bold text-gray-900">{stats.complianceRate}%</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{activity.driver}</p>
                    <p className="text-sm text-gray-600">{activity.action}</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">{activity.time}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: <BarChart3 className="w-4 h-4" />,
      content: overviewTab
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: <BarChart3 className="w-4 h-4" />,
      content: <AnalyticsDashboard />
    },
    {
      id: 'courses',
      label: 'Course Builder',
      icon: <BookOpen className="w-4 h-4" />,
      content: <CourseBuilder />
    },
    {
      id: 'users',
      label: 'User Management',
      icon: <UserPlus className="w-4 h-4" />,
      content: <UserManagement />
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings className="w-4 h-4" />,
      content: (
        <div className="p-8 text-center">
          <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Settings</h3>
          <p className="text-gray-600">System settings and configuration options coming soon.</p>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Administration Panel</h1>
          <p className="text-gray-600">Manage your organization's driver training program</p>
        </div>
        
        <Card>
          <CardContent className="p-6">
            <Tabs tabs={tabs} defaultTab="overview" variant="underline" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}